/**
 * Game Constants
 *
 * Centralized configuration for all game-related magic numbers.
 * This improves maintainability and makes it easier to balance the game.
 */

/**
 * Animation timing constants (in milliseconds)
 */
export const ANIMATION = {
  /** Duration of slime pop animation - aligned with 60fps frame (16.67ms) for smooth rendering */
  POP_DURATION: 16,
  /** Duration of shake animation for incorrect matches */
  SHAKE_DURATION: 25,
  /** Time after last match before combo resets */
  COMBO_BREAK_TIMEOUT: 1500,
  /** Duration of countdown animation at game start */
  COUNTDOWN_DURATION: 3500,
  /** Duration of floating animation cycle */
  FLOATING_DURATION: 2000,
} as const;

/**
 * Game balance and scoring constants
 */
export const GAME_BALANCE = {
  /** Combo count interval for time slime appearance */
  TIME_SLIME_COMBO_INTERVAL: 20,
  /** Score required to unlock additional slime types per panel */
  PANEL_UNLOCK_SCORE: 4000,
  /** Starting time in seconds */
  STARTING_TIME: 25,
  /** Maximum seconds added by matching time slime (at low scores) */
  TIME_SLIME_BONUS: 6,
  /** Minimum seconds added by matching time slime (at high scores) */
  MIN_TIME_SLIME_BONUS: 4,
  /** Score interval at which time bonus decreases by 1 second */
  TIME_BONUS_REDUCTION_INTERVAL: 20000,
  /** Base points for successful match */
  BASE_SCORE: 10,
  /** Points multiplied by combo count */
  COMBO_MULTIPLIER: 10,
  /** Score multiplier when bonus is active */
  BONUS_MULTIPLIER: 1.5,
} as const;

/**
 * Slime type progression constants
 */
export const SLIME_LIMITS = {
  /** Maximum slime types per panel */
  MAX_TYPES_PER_PANEL: 3,
  /** Total number of unique slime types */
  TOTAL_SLIME_TYPES: 6,
  /** Initial number of slime types at game start */
  INITIAL_TYPES: 2,
} as const;

/**
 * Bonus system constants
 */
export const BONUS_SYSTEM = {
  /** Maximum bonus tokens player can hold */
  MAX_TOKENS: 9,
  /** Tokens earned per ad watch */
  TOKENS_PER_AD: 3,
  /** Cost in tokens to activate bonus */
  TOKEN_COST: 1,
  /** Cooldown between ad watches in milliseconds */
  AD_COOLDOWN_MS: 60000,
} as const;

/**
 * Leaderboard constants
 */
export const LEADERBOARD = {
  /** Number of top scores to fetch */
  DEFAULT_LIMIT: 100,
  /** Maximum display name length */
  MAX_DISPLAY_NAME_LENGTH: 20,
  /** Minimum display name length */
  MIN_DISPLAY_NAME_LENGTH: 3,
} as const;

// Asserted against `^web-.*` by firestore.rules for the Poki platform;
// CrazyGames builds use `cg-*` and would require a parallel firestore.rules
// branch (`docId.matches('^cg_.+') && gameVersion.matches('^cg-.*')`)
// before any leaderboard write would succeed. Update rules before flipping
// the gameVersion if/when CG leaderboards go live.
export const GAME_VERSION = import.meta.env.VITE_PLATFORM === 'crazygames' ? 'cg-1.0' : 'web-1.0';

/**
 * Audio constants
 */
export const AUDIO = {
  /** Default background music volume */
  DEFAULT_MUSIC_VOLUME: 0.3,
  /** Default sound effects volume */
  DEFAULT_SFX_VOLUME: 0.5,
} as const;
